import { useAuthFetch, useGuardedEffect } from "@p0security/frontend-core";
import { DatabricksApiRoutes } from "@p0security/integrations-databricks-shared/api";
import { useCallback, useRef, useState } from "react";

import { errorMessage, readHeldAdminRequests } from "./options.ts";

/**
 * What P0 found when it checked which `admin` requests may still hold an
 * admin switch's role: their IDs, none meaning the switch can be turned
 * off, or why it couldn't check.
 */
export type HeldAdminRequestsCheck =
  | { type: "checked"; requestIds: string[] }
  | { type: "failed"; reason: string };

/**
 * How long to wait before asking again while requests still hold the role,
 * in milliseconds. They end on their own, or someone revokes them, while
 * the instructions stay open.
 */
export const RECHECK_INTERVAL = 10_000;

/**
 * Asks P0 which `admin` requests on `scope`, an installed account's or
 * workspace's ID, may still hold its role, and saves what it found as
 * `check` in the switch's instruction state, through `setState`. It renders
 * nothing: the instructions show the Terraform that takes the role away only
 * once no request holds it.
 *
 * While any does, it asks again every {@link RECHECK_INTERVAL}. Otherwise a
 * customer who waited for the requests to end would never see the Terraform:
 * "Next" would turn the switch off, and the principal would keep the role.
 */
export const HeldAdminRequestsChecker = ({
  scope,
  setState,
}: {
  scope: string;
  setState: (state: { check: HeldAdminRequestsCheck }) => unknown;
}) => {
  // The instructions' state setter is a new function on every render, so the
  // effect calls the latest one rather than checking again for each.
  const latestSetState = useRef(setState);
  latestSetState.current = setState;
  const report = useCallback((check: HeldAdminRequestsCheck) => {
    latestSetState.current({ check });
  }, []);
  // Stable, because the effect re-runs whenever its error handler changes.
  const onCheckError = useCallback(
    (error: unknown) => report({ type: "failed", reason: errorMessage(error) }),
    [report]
  );
  // `useAuthFetch` reports a failed fetch even after its cancellation fires,
  // so its handler only records why, and the effect reports it if its fetch
  // is still current. It's also called with nothing when a fetch succeeds.
  const fetchError = useRef<string>();
  const onFetchError = useCallback((error: string | undefined) => {
    fetchError.current = error;
  }, []);
  // Its own fetch, rather than the page's `context.authFetch`, which would
  // swap the instructions for a spinner, and so unmount this, while it runs.
  const authFetch = useAuthFetch(onFetchError);
  // Counts the checks made, so that scheduling the next one re-runs the
  // effect.
  const [checks, setChecks] = useState(0);

  useGuardedEffect(
    (cancellation) => async () => {
      const response = await authFetch(
        DatabricksApiRoutes.heldAdminRequests.path(scope),
        { cancellation }
      );
      if (!response) {
        if (fetchError.current)
          cancellation.guard(onCheckError)(fetchError.current);
        return;
      }
      const requestIds = await readHeldAdminRequests(response);
      cancellation.guard(report)({ type: "checked", requestIds });
      // Guarded, so a check scheduled before this unmounts never runs.
      if (requestIds.length)
        setTimeout(
          cancellation.guard(() => setChecks(checks + 1)),
          RECHECK_INTERVAL
        );
    },
    [authFetch, checks, onCheckError, report, scope],
    onCheckError
  );

  return null;
};
